import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { ensureMinimumOperationTime } from '@/lib/utils/timing'
import { getApi } from '@/stores/api'
import {
  COUPLING_STATE_VALUES,
  OFF_ROUTE_VALUES,
  ORDER_STATUS_VALUES,
  ORDER_TYPES,
  SOURCE_VALUES,
} from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { getUtcRangeForDate } from '@repo/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

const ORDERS_MINIMUM_OPERATION_TIME = 300

export const orderSearchSchema = z.object({
  keyword: z.string().optional(),
  date: z.string().optional(),
  status: z.array(z.enum(ORDER_STATUS_VALUES)).optional(),
  // Nova only, and deliberately declared here rather than on the backend route: the shared
  // validator would otherwise advertise a filter mainline has no column for. The Nova handler
  // reads it from the raw query and parses it itself.
  couplingState: z.array(z.enum(COUPLING_STATE_VALUES)).optional(),
  source: z.array(z.enum(SOURCE_VALUES)).optional(),
  flagged: z.boolean().optional(),
  offRoute: z.array(z.enum(OFF_ROUTE_VALUES)).optional(),
  area: z.array(z.string()).optional(),
  region: z.array(z.string()).optional(),
  territory: z.array(z.string()).optional(),
  customer: z.array(z.string()).optional(),
  banner: z.array(z.string()).optional(),
  outletSubtype: z.array(z.string()).optional(),
  type: z.enum(ORDER_TYPES).optional(),
  ...datatableSearchSchema.shape,
})

export const missingOrderSearchSchema = z.object({
  date: z.string().optional(),
  area: z.array(z.string()).optional(),
  region: z.array(z.string()).optional(),
  sapErp: z.string().optional(),
  territory: z.array(z.string()).optional(),
  crType: z.array(z.string()).optional(),
  outletSubtype: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export const returnsSearchSchema = z.object({
  keyword: z.string().optional(),
  date: z.string().optional(),
  status: z.array(z.enum(ORDER_STATUS_VALUES)).optional(),
  area: z.array(z.string()).optional(),
  region: z.array(z.string()).optional(),
  territory: z.array(z.string()).optional(),
  customer: z.array(z.string()).optional(),
  banner: z.array(z.string()).optional(),
  outletSubtype: z.array(z.string()).optional(),
  type: z.enum(ORDER_TYPES).optional(),
  ...datatableSearchSchema.shape,
})

const releaseOrderSchema = z.object({
  po_number: z.string(),
  sap_erp: z.string(),
})

export const releaseOrdersBodySchema = z.array(releaseOrderSchema)

const updateQuantitySchema = z.object({
  po_number: z.string(),
  sap_erp: z.string(),
  quantity_items: z.array(
    z.object({
      id: z.number(),
      material: z.string(),
      qty_chg: z.number(),
    }),
  ),
})

export const updateQuantitiesBodySchema = z.array(updateQuantitySchema)

export type OrderSearch = z.infer<typeof orderSearchSchema>
export type MissingOrderSearch = z.infer<typeof missingOrderSearchSchema>
export type ReturnsSearch = z.infer<typeof returnsSearchSchema>
export type ReleaseOrdersBody = z.infer<typeof releaseOrdersBodySchema>
export type updateQuantitiesBody = z.infer<typeof updateQuantitiesBodySchema>

async function getOrders(search: OrderSearch, signal?: AbortSignal) {
  const api = getApi()

  const query = { ...search } as OrderSearch & {
    dateStartUtc: string | undefined
    dateEndUtc: string | undefined
  }

  if (search.date) {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const utcRangeForDate = getUtcRangeForDate(search.date, userTimeZone)

    if (utcRangeForDate.start && utcRangeForDate.end) {
      query.dateStartUtc = utcRangeForDate.start
      query.dateEndUtc = utcRangeForDate.end
    }
  }

  const res = await api.orders.$get(
    {
      query: stringifyValues(query),
    },
    { init: { signal } },
  )

  const orders = await res.json()
  return orders
}

async function getOrdersCsv(search: OrderSearch, signal?: AbortSignal) {
  const query = { ...search } as OrderSearch & {
    dateStartUtc: string | undefined
    dateEndUtc: string | undefined
  }

  if (search.date) {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const utcRangeForDate = getUtcRangeForDate(search.date, userTimeZone)

    if (utcRangeForDate.start && utcRangeForDate.end) {
      query.dateStartUtc = utcRangeForDate.start
      query.dateEndUtc = utcRangeForDate.end
    }
  }

  const queryParams = new URLSearchParams(stringifyValues(query)).toString()

  const csvExport = await fetchCsvExport(`/orders/export?${queryParams}`, signal)
  return csvExport
}

async function getMissingOrdersCsv(search: MissingOrderSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  const csvExport = await fetchCsvExport(`/orders/export/missing?${queryParams}`, signal)
  return csvExport
}

async function getOrderItemsCsv(dynamoId: string, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(`/orders/export/${dynamoId}/items`, signal)
  return csvExport
}

async function getOrderDetails(dynamoId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.orders[':dynamoId'].$get(
    {
      param: { dynamoId },
    },
    { init: { signal } },
  )

  const orderDetails = await res.json()
  return orderDetails
}

async function getMissingOrders(search: MissingOrderSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.orders.missing.$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  const missingOrders = await res.json()
  return missingOrders
}

async function getReturns(search: ReturnsSearch, signal?: AbortSignal) {
  const api = getApi()

  const query = { ...search } as ReturnsSearch & {
    dateStartUtc: string | undefined
    dateEndUtc: string | undefined
  }

  if (search.date) {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const utcRangeForDate = getUtcRangeForDate(search.date, userTimeZone)

    if (utcRangeForDate.start && utcRangeForDate.end) {
      query.dateStartUtc = utcRangeForDate.start
      query.dateEndUtc = utcRangeForDate.end
    }
  }

  const res = await api.orders.returns.$get(
    {
      query: stringifyValues(query),
    },
    { init: { signal } },
  )

  const returns = await res.json()
  return returns
}

async function getCrTypes(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.orders['cr-types'].$get({}, { init: { signal } })

  const crTypes = await res.json()
  return crTypes
}

export async function releaseOrders(orders: ReleaseOrdersBody) {
  const startTime = Date.now()

  const api = getApi()
  const res = await api.orders.release.$post({
    json: orders,
  })

  await ensureMinimumOperationTime(startTime, ORDERS_MINIMUM_OPERATION_TIME)

  const response = await res.json()
  return response
}

export async function updateQuantities(payload: updateQuantitiesBody) {
  const startTime = Date.now()

  const api = getApi()
  const res = await api.orders['update-quantity'].$post({
    json: payload,
  })

  await ensureMinimumOperationTime(startTime, ORDERS_MINIMUM_OPERATION_TIME)

  const response = await res.json()
  return response
}

export const ordersExportQueryOptions = (queries: OrderSearch) => ({
  queryKey: ['ordersExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getOrdersCsv(queries, signal),
})

export const missingOrdersExportQueryOptions = (queries: MissingOrderSearch) => ({
  queryKey: ['missingOrdersExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getMissingOrdersCsv(queries, signal),
})

export const orderItemsExportQueryOptions = (dynamoId: string) => ({
  queryKey: ['orderItemsExport', dynamoId],
  queryFn: ({ signal }: { signal: AbortSignal }) => getOrderItemsCsv(dynamoId, signal),
})

export function useOrders(queries: OrderSearch) {
  return useQuery({
    queryKey: ['orders', queries],
    queryFn: ({ signal }) => getOrders(queries, signal),
  })
}

export const orderDetailsQueryOptions = (dynamoId: string) => ({
  queryKey: ['orderDetails', dynamoId],
  queryFn: ({ signal }: { signal: AbortSignal }) => getOrderDetails(dynamoId, signal),
})

export function useOrderDetails(dynamoId: string) {
  return useQuery(orderDetailsQueryOptions(dynamoId))
}

export function useMissingOrders(queries: MissingOrderSearch) {
  return useQuery({
    queryKey: ['missingOrders', queries],
    queryFn: ({ signal }) => getMissingOrders(queries, signal),
  })
}

export function useReturns(queries: ReturnsSearch) {
  return useQuery({
    queryKey: ['returns', queries],
    queryFn: ({ signal }) => getReturns(queries, signal),
  })
}

export function useReleaseOrders({ handleOnSuccess, handleOnError }: QueryHandlers = {}) {
  const { t } = useTranslation(['orders', 'common'])
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['orders-release'],
    mutationFn: releaseOrders,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orderDetails'] }),
        queryClient.invalidateQueries({ queryKey: ['orders'] }),
      ])

      handleOnSuccess?.()

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('flaggedOrder.releaseOrder.success'),
        className: toastClassName,
      })
    },
    onError: (error) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: error.message,
        className: toastClassName,
      })

      handleOnError?.()
    },
  })
}

export function useUpdateQuantities({ handleOnSuccess, handleOnError }: QueryHandlers = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['orders-update-quantities'],
    mutationFn: updateQuantities,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orderDetails'] })

      handleOnSuccess?.()

      toast.success('Success', {
        description: `Quantites have been adjusted.`,
        className: toastClassName,
      })
    },
    onError: (error) => {
      console.log(error)
      toast.error('Error', {
        description: error.message,
        className: toastClassName,
      })

      handleOnError?.()
    },
  })
}

export function useCrTypes() {
  return useQuery({
    queryKey: ['crTypes'],
    queryFn: ({ signal }) => getCrTypes(signal),
  })
}

/*
 * Nova-only order mutations. Both endpoints live in the Nova backend module and exist on no
 * mainline environment, so only the Nova order-details tree calls them.
 */

export type UpdateDeliveryDateBody = {
  delivery_date: string
  /** Every leg the date is being set on — both legs of a coupled order, otherwise just the one. */
  orders: { po_number: string; sap_erp: string }[]
}

/**
 * One request carrying every leg the date applies to. The backend fans out to Fuze Core per leg and
 * answers once, so a coupled edit is a single round trip with a single outcome.
 */
export async function updateDeliveryDate(payload: UpdateDeliveryDateBody) {
  const startTime = Date.now()

  const api = getApi()
  const res = await api.orders['delivery-date'].$patch({
    json: payload,
  })

  await ensureMinimumOperationTime(startTime, ORDERS_MINIMUM_OPERATION_TIME)

  const response = await res.json()
  return response
}

export function useUpdateDeliveryDate(dynamoId: string) {
  const { t } = useTranslation('orders')
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['orders-update-delivery-date', dynamoId],
    mutationFn: updateDeliveryDate,
    // Refetched even on failure: a coupled edit can move one leg and fail the other, and the view
    // has to show the resulting mismatch rather than the dates it had before the attempt.
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['orderDetails', dynamoId],
      })
    },
    onSuccess: () => {
      toast.success(t('orderDetails.deliveryDate.updated'), {
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('orderDetails.deliveryDate.error'), {
        className: toastClassName,
      })
    },
  })
}

async function uncoupleOrderFn(params: { dynamoId: string; reason: string }) {
  const api = getApi()
  const res = await api.orders.uncouple.$post({
    json: params,
  })

  const response = await res.json()
  return response
}

export function useUncoupleOrder(dynamoId: string, { handleOnSuccess }: QueryHandlers) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['orders', 'common'])

  return useMutation({
    mutationKey: ['orders-uncouple', dynamoId],
    mutationFn: uncoupleOrderFn,
    onSuccess: async () => {
      handleOnSuccess?.()

      await queryClient.invalidateQueries({
        queryKey: ['orderDetails', dynamoId],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('coupledOrder.uncoupleSuccess'),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('coupledOrder.uncoupleError'),
        className: toastClassName,
      })
    },
  })
}
